/**
 * GuestLinkSelector — compact guest-profile link control. A selector replaces
 * three large tiles so the profile stays calm while invitation, confirmation,
 * and My Celebration links remain unmistakably distinct.
 *
 * Blue "Invitation" vs gold "Confirmation" — the same identity used in the
 * Communications Hub (guestLinkTokens.js). Shows the selected link + a Copy
 * button; when a token doesn't exist yet, offers to create the confirmation
 * links (idempotent; never touches the RSVP token).
 */
import React, { useState } from 'react';
import { LINK_KINDS, LINK_ORDER, buildGuestLink } from './guestLinkTokens';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export default function GuestLinkSelector({ guest, onTokensEnsured }) {
  const [kind, setKind] = useState('my_celebration');
  const [copied, setCopied] = useState(false);
  const [ensuring, setEnsuring] = useState(false);
  // Local override so a freshly-minted confirmation token appears immediately,
  // without waiting on a parent re-fetch.
  const [freshCelebrationToken, setFreshCelebrationToken] = useState(null);
  const [freshMyCelebrationToken, setFreshMyCelebrationToken] = useState(null);
  const effectiveGuest = {
    ...guest,
    ...(freshCelebrationToken ? { party_celebration_token: freshCelebrationToken } : {}),
    ...(freshMyCelebrationToken ? { my_celebration_token: freshMyCelebrationToken } : {}),
  };
  const meta = LINK_KINDS[kind];
  const url = buildGuestLink(kind, effectiveGuest);
  const hasToken = !!url;

  const copy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* clipboard unavailable */ }
  };

  const ensureConfirmation = async () => {
    setEnsuring(true);
    try {
      await fetch(`${API}/api/celebration/tokens/ensure`, { method: 'POST' });
      // Re-read THIS guest's now-minted confirmation token so the link renders
      // and is copyable right away (no lingering "No confirmation link yet").
      try {
        const res = await fetch(`${API}/api/guests`);
        const list = await res.json();
        const guests = Array.isArray(list) ? list : (list && list.guests) || [];
        const me = guests.find((g) => String(g.id) === String(guest.id));
        if (me && me.party_celebration_token) setFreshCelebrationToken(me.party_celebration_token);
      } catch { /* keep the ensure result; empty state remains only if truly unminted */ }
      if (onTokensEnsured) await onTokensEnsured();
    } catch { /* surfaced by the empty state remaining */ } finally { setEnsuring(false); }
  };

  const ensureMyCelebration = async () => {
    setEnsuring(true);
    try {
      await fetch(`${API}/api/my-celebration/tokens/ensure`, { method: 'POST' });
      try {
        const res = await fetch(`${API}/api/guests`);
        const list = await res.json();
        const guests = Array.isArray(list) ? list : (list && list.guests) || [];
        const me = guests.find((g) => String(g.id) === String(guest.id));
        if (me && me.my_celebration_token) setFreshMyCelebrationToken(me.my_celebration_token);
      } catch { /* a re-fetch from the parent will still surface the link */ }
      if (onTokensEnsured) await onTokensEnsured();
    } catch { /* keep a compact, honest empty state */ } finally { setEnsuring(false); }
  };

  return (
    <div data-testid="guest-link-selector" style={{ border: '1px solid rgba(122,106,64,0.2)', borderRadius: 12, padding: 12, background: 'rgba(255,255,255,0.6)' }}>
      <div style={{ fontFamily: 'Cinzel, serif', fontSize: 11, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#7a6a40', marginBottom: 8 }}>
        Guest Links
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontFamily: 'Inter, system-ui, sans-serif' }}>
        <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: '50%', background: meta.color, boxShadow: `0 0 0 3px ${meta.colorSoft}` }} />
        <select data-testid="guest-link-kind-select" value={kind} onChange={(event) => { setKind(event.target.value); setCopied(false); }} style={{ flex: 1, minHeight: 40, borderRadius: 9, padding: '0 10px', border: `1.5px solid ${meta.colorBorder}`, background: meta.colorSoft, color: meta.color, fontSize: 13, fontWeight: 700 }}>
          {LINK_ORDER.map((k) => <option key={k} value={k}>{LINK_KINDS[k].title}</option>)}
        </select>
      </label>

      {/* the selected link + copy (or the create-links empty state) */}
      {hasToken ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              flex: 1, minWidth: 0, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5,
              color: meta.color, background: meta.colorSoft, border: `1px solid ${meta.colorBorder}`,
              borderRadius: 8, padding: '8px 10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }} title={url}>
              {url}
            </div>
            <button
              type="button"
              data-testid="guest-link-copy"
              onClick={copy}
              style={{
                cursor: 'pointer', borderRadius: 8, padding: '8px 14px', whiteSpace: 'nowrap',
                border: 'none', background: meta.color, color: '#fff',
                fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, fontWeight: 600,
              }}
            >
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#8a8a8a', marginTop: 6, fontFamily: 'Inter, system-ui, sans-serif' }}>
            {meta.hint}
          </div>
        </>
      ) : kind === 'confirmation' || kind === 'my_celebration' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ flex: 1, fontSize: 12, color: '#8a6a2f', fontFamily: 'Inter, system-ui, sans-serif' }}>
            {kind === 'my_celebration' ? 'No My Celebration link yet for this guest.' : 'No confirmation link yet for this party.'}
          </span>
          <button
            type="button"
            data-testid="guest-link-create"
            disabled={ensuring}
            onClick={kind === 'my_celebration' ? ensureMyCelebration : ensureConfirmation}
            style={{ cursor: 'pointer', borderRadius: 8, padding: '8px 12px', border: `1.5px solid ${meta.colorBorder}`, background: meta.colorSoft, color: meta.color, fontSize: 12, fontWeight: 600, fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            {ensuring ? 'Creating…' : `Create ${meta.short}`}
          </button>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: '#a06060', fontFamily: 'Inter, system-ui, sans-serif' }}>
          This guest isn't in a party with an invitation token yet.
        </div>
      )}
    </div>
  );
}
